import { useEffect, useMemo, useState } from "react"
import { Label } from "@/components/ui/label"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { ShoppingCart, FileText, ArrowRight, Loader as Loader2 } from "lucide-react"
import { useCantileverStore } from "@/lib/cantilever-store"
import { useLanguageStore } from "@/lib/language-store"
import { useAuthStore } from "@/lib/auth-store"
import { usePricingStore, resolvePricingContext } from "@/lib/pricing-store"
import { useDomainStore } from "@/lib/domain-store"
import { fetchCantileverMaterials } from "@/lib/cantilever-materials-service"
import { calculateCantileverBOM, CantileverBOMSummary, CantileverMaterialData } from "@/lib/cantilever-bom"
import { CantileverBOMDialog } from "@/components/cantilever-bom-dialog"
import { OrderQuotationDialog } from "@/components/order-quotation-dialog"
import { SaveToCartDialog } from "@/components/save-to-cart-dialog"
import { saveCantileverConfiguration } from "@/lib/cantilever-save-service"
import {
  ColumnType,
  ArmType,
  ColumnCoating,
  ArmCoating,
} from "@/lib/types"

const COLUMN_TYPES: ColumnType[] = ["IPE180", "IPE200", "IPE220", "IPE240", "IPE300"]
const ARM_TYPES: ArmType[] = ["IPE100", "IPE120", "IPE140", "IPE160"]
const CLEARANCE_OPTIONS = [200, 250, 300, 350, 400, 450, 500, 600, 700, 800, 900, 1000]
const BAY_WIDTHS = [500, 600, 700, 800, 900, 1000, 1100, 1200, 1300, 1400, 1500, 1600, 1700, 1800, 1900, 2000, 2100, 2200, 2300, 2400, 2500]

export function CantileverControls() {
  const { getTranslation } = useLanguageStore()
  const { user, profile, reseller } = useAuthStore()
  const { simulatedReseller } = usePricingStore()
  const { domainReseller, domainChecked } = useDomainStore()

  const isLoggedIn = !!user
  const isAdmin = profile?.role === "admin"
  const isReseller = profile?.role === "reseller"
  const isPublicDomain = domainChecked && !!domainReseller && !isLoggedIn

  const [materials, setMaterials] = useState<CantileverMaterialData[]>([])
  const [bom, setBOM] = useState<CantileverBOMSummary | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [saveResult, setSaveResult] = useState<{ config_id: string; label: string; price: number } | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [showBOMDialog, setShowBOMDialog] = useState(false)
  const [showOrderDialog, setShowOrderDialog] = useState(false)

  const {
    config,
    setRackType,
    setColumnType,
    setArmType,
    setColumnHeight,
    setArmLength,
    setNumberOfArmLevels,
    setNumberOfBays,
    setClearanceTopLevel,
    setBayWidth,
    setColumnCoating,
    setArmCoating,
  } = useCantileverStore()

  useEffect(() => {
    fetchCantileverMaterials().then(setMaterials)
  }, [])

  const role = profile?.role ?? null
  const pricingContext = useMemo(
    () => resolvePricingContext(role, reseller, simulatedReseller, domainReseller),
    [role, reseller?.discount_percent, reseller?.margin_percent, simulatedReseller?.id, domainReseller?.id, domainChecked]
  )

  useEffect(() => {
    if (materials.length === 0) return
    const newBOM = calculateCantileverBOM(materials, config, pricingContext ?? undefined)
    setBOM(newBOM)
  }, [materials, config, pricingContext, domainChecked])

  const resellerShopUrl = isReseller ? (reseller?.shop_url ?? null) : null
  const resellerCartUrl = isReseller ? (reseller?.cart_url ?? null) : null
  const resellerHasCart = !!(resellerShopUrl || resellerCartUrl)
  const domainShopUrl = isPublicDomain && domainReseller?.shop_url ? domainReseller.shop_url : null
  const domainCartUrl = isPublicDomain && domainReseller?.cart_url && domainReseller?.cart_api_key_id ? domainReseller.cart_url : null
  const domainHasCart = !!(domainShopUrl || domainCartUrl)

  const handleAddToCart = async () => {
    if (!bom) return
    setIsSaving(true)
    setSaveResult(null)
    setSaveError(null)

    const label = `Cantilever ${config.rackType} ${config.columnType} — ${config.numberOfBays} bays × ${config.numberOfArmLevels} levels, H${config.columnHeight}mm`
    const isPublicDomainSave = !isReseller && !isAdmin && domainHasCart

    const { data, error } = await saveCantileverConfiguration({
      label,
      price: bom.totalPrice,
      config,
      bom,
      useEdgeFunction: isPublicDomainSave,
    })

    setIsSaving(false)

    if (error || !data) {
      setSaveError(error ?? "Unknown error")
      setShowSaveDialog(true)
      return
    }

    const activeShopUrl = resellerShopUrl ?? domainShopUrl
    if (activeShopUrl) {
      const sep = activeShopUrl.includes("?") ? "&" : "?"
      window.location.href = `${activeShopUrl}${sep}add_config=1&configurator=cantilever&config_id=${encodeURIComponent(data.config_id)}`
      return
    }

    const activeLegacyCartUrl = resellerCartUrl ?? domainCartUrl
    if (activeLegacyCartUrl) {
      window.location.href = activeLegacyCartUrl.replace("{config_id}", encodeURIComponent(data.config_id))
      return
    }

    setSaveResult(data)
    setShowSaveDialog(true)
  }

  const totalLengthM = ((config.numberOfBays - 1) * config.bayWidth / 1000).toFixed(1)
  const totalHeightM = (config.columnHeight / 1000).toFixed(1)

  return (
    <Card className="border border-gray-200 rounded-xl shadow-sm">
      <CardContent className="pt-3">
        {isReseller && reseller && (
          <p className="text-sm font-semibold text-center mb-1 tracking-wide" style={{ color: reseller.brand_color ?? "#212b4f" }}>
            {reseller.name}
          </p>
        )}
        {isAdmin && simulatedReseller && (
          <p className="text-sm font-semibold text-center mb-1 tracking-wide" style={{ color: simulatedReseller.brand_color ?? "#212b4f" }}>
            {simulatedReseller.name} <span className="font-normal text-xs opacity-70">(simulation)</span>
          </p>
        )}
        {isPublicDomain && (
          <p className="text-sm font-semibold text-center mb-1 tracking-wide" style={{ color: domainReseller!.brand_color ?? "#212b4f" }}>
            {domainReseller!.name}
          </p>
        )}

        <h2 className="text-xl font-bold text-[#212b4f] text-center mb-4">3D Cantilever Rack Configurator</h2>

        <div className="space-y-3">
          {/* Rack Type */}
          <div className="space-y-1.5">
            <Label className="text-foreground text-sm font-medium">{getTranslation("rack.type")}</Label>
            <div className="flex gap-2">
              {(["single", "double"] as const).map((v) => (
                <Button
                  key={v}
                  variant={config.rackType === v ? "brand-solid" : "brand-outline"}
                  onClick={() => setRackType(v)}
                  className="h-8 flex-1 text-sm border border-[#212b4f] px-2 py-1 rounded-2xl"
                >
                  {getTranslation(v === "single" ? "single.sided" : "double.sided")}
                </Button>
              ))}
            </div>
          </div>

          {/* Column Type */}
          <div className="space-y-1.5">
            <Label className="text-foreground text-sm font-medium">{getTranslation("column.type")}</Label>
            <select
              value={config.columnType}
              onChange={(e) => setColumnType(e.target.value as ColumnType)}
              className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#212b4f]/30 focus:border-[#212b4f]"
            >
              {COLUMN_TYPES.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>

          {/* Arm Type */}
          <div className="space-y-1.5">
            <Label className="text-foreground text-sm font-medium">{getTranslation("arm.type")}</Label>
            <select
              value={config.armType}
              onChange={(e) => setArmType(e.target.value as ArmType)}
              className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#212b4f]/30 focus:border-[#212b4f]"
            >
              {ARM_TYPES.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>

          {/* Sliders: 2×2 grid */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            {/* Column Height */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <Label className="text-foreground text-sm font-medium">{getTranslation("column.height")}</Label>
                <span className="text-base font-bold text-foreground">{config.columnHeight} mm</span>
              </div>
              <Slider
                value={[config.columnHeight]}
                onValueChange={(v) => setColumnHeight(v[0])}
                min={3000}
                max={8000}
                step={500}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>3000</span>
                <span>8000</span>
              </div>
            </div>

            {/* Arm Length */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <Label className="text-foreground text-sm font-medium">{getTranslation("arm.length")}</Label>
                <span className="text-base font-bold text-foreground">{config.armLength} mm</span>
              </div>
              <Slider
                value={[config.armLength]}
                onValueChange={(v) => setArmLength(v[0])}
                min={600}
                max={2000}
                step={100}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>600</span>
                <span>2000</span>
              </div>
            </div>

            {/* Arm Levels */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <Label className="text-foreground text-sm font-medium">{getTranslation("arm.levels")}</Label>
                <span className="text-base font-bold text-foreground">{config.numberOfArmLevels}</span>
              </div>
              <Slider
                value={[config.numberOfArmLevels]}
                onValueChange={(v) => setNumberOfArmLevels(v[0])}
                min={2}
                max={8}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>2</span>
                <span>8</span>
              </div>
            </div>

            {/* Number of Bays */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <Label className="text-foreground text-sm font-medium">{getTranslation("number.of.bays")}</Label>
                <span className="text-base font-bold text-foreground">{config.numberOfBays}</span>
              </div>
              <Slider
                value={[config.numberOfBays]}
                onValueChange={(v) => setNumberOfBays(v[0])}
                min={2}
                max={25}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>2</span>
                <span>25</span>
              </div>
            </div>
          </div>

          {/* Clearance Top Level */}
          <div className="space-y-1.5">
            <Label className="text-foreground text-sm font-medium">{getTranslation("clearance.top")}</Label>
            <select
              value={config.clearanceTopLevel}
              onChange={(e) => setClearanceTopLevel(Number(e.target.value))}
              className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#212b4f]/30 focus:border-[#212b4f]"
            >
              {CLEARANCE_OPTIONS.map((v) => (
                <option key={v} value={v}>{v} mm</option>
              ))}
            </select>
          </div>

          {/* Bay Width */}
          <div className="space-y-1.5">
            <Label className="text-foreground text-sm font-medium">{getTranslation("bay.width")}</Label>
            <select
              value={config.bayWidth}
              onChange={(e) => setBayWidth(Number(e.target.value))}
              className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#212b4f]/30 focus:border-[#212b4f]"
            >
              {BAY_WIDTHS.map((v) => (
                <option key={v} value={v}>{v} mm</option>
              ))}
            </select>
          </div>

          {/* Column Coating */}
          <div className="space-y-1.5">
            <Label className="text-foreground text-sm font-medium">{getTranslation("column.coating")}</Label>
            <div className="flex gap-2">
              {(["RAL 5019 Blue", "Hot Dipped Galvanized"] as ColumnCoating[]).map((v) => (
                <Button
                  key={v}
                  variant={config.columnCoating === v ? "brand-solid" : "brand-outline"}
                  onClick={() => setColumnCoating(v)}
                  className="h-8 flex-1 text-xs border border-[#212b4f] px-2 py-1 rounded-2xl"
                >
                  {v === "RAL 5019 Blue" ? getTranslation("coating.blue") : getTranslation("coating.galvanized")}
                </Button>
              ))}
            </div>
          </div>

          {/* Arm Coating */}
          <div className="space-y-1.5">
            <Label className="text-foreground text-sm font-medium">{getTranslation("arm.coating")}</Label>
            <div className="flex gap-2">
              {(["RAL2008 Orange", "Hot Dipped Galvanized"] as ArmCoating[]).map((v) => (
                <Button
                  key={v}
                  variant={config.armCoating === v ? "brand-solid" : "brand-outline"}
                  onClick={() => setArmCoating(v)}
                  className="h-8 flex-1 text-xs border border-[#212b4f] px-2 py-1 rounded-2xl"
                >
                  {v === "RAL2008 Orange" ? getTranslation("coating.orange") : getTranslation("coating.galvanized")}
                </Button>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-1.5 border border-gray-200 mt-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span className="font-medium">{getTranslation("summary.total.length")}</span>
              <span>{totalLengthM} m</span>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span className="font-medium">{getTranslation("summary.total.height")}</span>
              <span>{totalHeightM} m</span>
            </div>
            {bom && bom.maxLoadPerArm > 0 && (
              <div className="flex justify-between text-sm text-gray-600">
                <span className="font-medium">{getTranslation("summary.max.load.arm")}</span>
                <span className="text-orange-600 font-semibold">{bom.maxLoadPerArm} kg</span>
              </div>
            )}
          </div>

          {/* Price */}
          <div className="pt-1 flex items-center justify-center">
            {(isLoggedIn || isPublicDomain) && bom ? (
              <div className="font-bold text-3xl text-green-600">€{Math.round(bom.totalPrice)}</div>
            ) : (
              <div className="text-sm font-medium text-slate-500 italic">Price on request</div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            {(isReseller || isPublicDomain) && (
              <Button
                onClick={() => setShowOrderDialog(true)}
                className="w-full gap-2 bg-[#2e78c7] hover:bg-[#2568b0] text-white h-9 text-sm px-3 py-1.5 rounded-2xl"
              >
                <ArrowRight className="h-4 w-4" />
                {getTranslation("proceed.with.order")}
              </Button>
            )}

            {isAdmin && (
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => setShowBOMDialog(true)}
                  variant="outline"
                  className="gap-2 h-9 text-sm px-3 py-1.5 border-[#212b4f] text-[#212b4f] hover:bg-[#212b4f] hover:text-white rounded-2xl"
                >
                  <FileText className="h-4 w-4" />
                  {getTranslation("bom.button")}
                </Button>
                <Button
                  onClick={handleAddToCart}
                  disabled={isSaving || !bom}
                  className="gap-2 bg-green-600 hover:bg-green-700 text-white h-9 text-sm px-3 py-1.5 rounded-2xl disabled:opacity-60"
                >
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                  {getTranslation("add.to.cart")}
                </Button>
              </div>
            )}

            {isReseller && resellerHasCart && (
              <Button
                onClick={handleAddToCart}
                disabled={isSaving || !bom}
                className="w-full gap-2 bg-green-600 hover:bg-green-700 text-white h-10 text-sm rounded-2xl disabled:opacity-60"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                {getTranslation("add.to.cart")}
              </Button>
            )}

            {isPublicDomain && domainHasCart && (
              <Button
                onClick={handleAddToCart}
                disabled={isSaving || !bom}
                className="w-full gap-2 bg-green-600 hover:bg-green-700 text-white h-10 text-sm rounded-2xl disabled:opacity-60"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                {getTranslation("add.to.cart")}
              </Button>
            )}
          </div>
        </div>

        <CantileverBOMDialog open={showBOMDialog} onOpenChange={setShowBOMDialog} bom={bom} />
        <OrderQuotationDialog open={showOrderDialog} onOpenChange={(open) => setShowOrderDialog(open)} bom={bom} />
        <SaveToCartDialog
          open={showSaveDialog}
          onOpenChange={setShowSaveDialog}
          configId={saveResult?.config_id ?? null}
          label={saveResult?.label ?? null}
          price={saveResult?.price ?? null}
          error={saveError}
        />
      </CardContent>
    </Card>
  )
}
